/**
 * Client-side arrival-window derivation — a faithful port of the backend
 * scheduling_app/arrival_windows.py::derive_grid. The promise a customer hears
 * (voice + SMS) is generated server-side from the SAME rule, so this preview
 * must not drift: a window the UI shows that voice would not say is a lie.
 *
 * Keep MIN_TAIL_MINUTES and the fold/clamp rules in lockstep with the backend.
 */

// A tail shorter than this folds into the previous window — "4:45 to 5" is a
// time wearing a window's clothes, not a window.
export const MIN_TAIL_MINUTES = 45;

// Backend ArrivalWindowPolicy bound.
export const WINDOW_MINUTES_MIN = 30;
export const WINDOW_MINUTES_MAX = 720;
export const DEFAULT_WINDOW_MINUTES = 180;

/**
 * Windows for one open day, as [startMinute, endMinute] pairs.
 * 7:00-17:00 @ 180 -> [[420,600],[600,780],[780,960],[960,1020]] (7-10,10-1,1-4,4-5).
 * Nothing ends past close; a sub-MIN_TAIL remainder folds into the window before it.
 */
export function deriveWindows(openMinute, closeMinute, windowMinutes) {
  if (!(closeMinute > openMinute) || !(windowMinutes > 0)) return [];
  const out = [];
  let cursor = openMinute;
  while (cursor < closeMinute) {
    const end = Math.min(cursor + windowMinutes, closeMinute);
    out.push([cursor, end]);
    cursor = end;
  }
  if (out.length > 1 && out[out.length - 1][1] - out[out.length - 1][0] < MIN_TAIL_MINUTES) {
    const tail = out.pop();
    out[out.length - 1][1] = tail[1];
  }
  return out;
}

/** "10", "1", "4:30" — hours only, 12-hour, no am/pm (the sentence carries that). */
export function hourLabel(minute) {
  const hour = Math.floor(minute / 60) % 24;
  const h12 = hour % 12 || 12;
  const m = minute % 60;
  return m ? `${h12}:${String(m).padStart(2, '0')}` : String(h12);
}

/** "10–1" for a [start,end] pair. */
export function windowLabel([s, e]) {
  return `${hourLabel(s)}–${hourLabel(e)}`;
}

/** Clamp a stored window_minutes to the backend-accepted range, else the default. */
export function normalizeWindowMinutes(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= WINDOW_MINUTES_MIN && n <= WINDOW_MINUTES_MAX ? n : DEFAULT_WINDOW_MINUTES;
}
